import ExtendedInfo from 'views/CustomerPage/OrderHistory/ExtendedInfo';
import styles from './styles.scss';
import { Button, LoaderButton } from 'components/Button';
import { scrollToTarget, NO_ELEMENT, rm } from 'utils';
import { Address } from '../Components/Address/Address';
import MessageView from '../Components/MessageView';
import { postSendReturn } from 'services/ApiCustomerSupportFormService';
import { ChangeEvent, useEffect, useRef } from 'react';
import { RadioButton, TextArea } from 'components/Shared/Form';
import { useReducer } from 'react';
import { useToggle } from 'hooks';
import {
  reducer,
  isValid,
  handleReasonChange,
  handleConditionChange,
  productChanged,
  getItemConditions,
  getReturnReasons,
  returnsInitialState
} from './ReturnsHelper';
import {
  addressChanged,
  addressLookupValidationError,
  contactOptionChanged,
  OrderFormProps
} from '../Utils';
import { IBasicResponse } from 'models/IBasicResponse';
import { DynamicImageUploader } from 'components/CustomerSupportForms/Components/ImageUpload/DynamicImageUploader';
import {
  BlueSectionContainer,
  ErrorContainer,
  RadioBtnContainer,
  SubmitContainer
} from 'components/CustomerSupportForms/Components/SectionContainer/SectionContainer';
import { DisplayMode } from 'views/CustomerPage/OrderHistory/OrderLine';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';

const OPTIONAL_MSG_MAX_LENGTH = 1000;
const MAX_IMAGE_COUNT = 5;

export const ReturnsForm = ({ order, error, returnUrl, onSuccess }: OrderFormProps) => {
  const anchorRef = useRef(NO_ELEMENT);
  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [internalState, internalDispatch] = useReducer(reducer, returnsInitialState);
  const [addressChange, toggleAddressChange] = useToggle(false);
  const [loading, toggleLoading] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order?.deliveryAddress });
      internalDispatch({ type: 'email', value: order?.email });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [error, order]);

  useEffect(() => {
    internalDispatch({ type: 'addressError', value: '' });
  }, [addressChange]);

  const {
    address,
    email,
    optionalMsg,
    products,
    contactOption,
    images,
    itemCondition,
    reasonForReturn
  } = internalState;
  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });
    if (isValid(internalState, addressChange, internalDispatch)) {
      if (address && email && order) {
        return postSendReturn({
          address,
          email,
          orderNumber: order.orderNumber,
          message: optionalMsg,
          files: images.map(img => img.imageUrl),
          packageStatus: itemCondition,
          reason: reasonForReturn,
          products: products.filter(p => p.quantity > 0), // Remove those with quantity 0
          hash: order.hash,
          customerContactOption: contactOption
        })
          .then(() => {
            toggleLoading();
            onSuccess();
            scrollToTopIfNeeded();
            reduxDispatch(GtmCustomerSupportFormSubmit(FormType.Returns));
          })
          .catch((response: IBasicResponse) => {
            toggleLoading();
            internalDispatch({ type: 'generalError', value: response.message[0] });
          });
      }
    }

    toggleLoading();
    return Promise.resolve();
  };

  if (internalState.hasError) {
    return (
      <MessageView
        title={rm('CustomerPage_Return_Error_Title')}
        message={rm('CustomerPage_Return_Error_Msg')}
        returnUrl={returnUrl}
      />
    );
  }

  if (order?.orderNumber === '' || order?.orderNumber === undefined) {
    return (
      <MessageView
        title={rm('CustomerPage_Empty_Return_Title')}
        message={rm('CustomerPage_Empty_Return_Info')}
        returnUrl={returnUrl}
      />
    );
  }

  const reasonIsChecked = (value: string) => {
    return value === internalState.reasonForReturn;
  };

  const conditionIsChecked = (value: string) => {
    return value === internalState.itemCondition;
  };

  const handleMsgInput = (_event: ChangeEvent<HTMLTextAreaElement>, newValue: string) => {
    internalDispatch({
      type: 'optionalMsg',
      value: newValue
    });
  };

  return (
    <>
      <h3>{rm('customerpage_returns_products_header')}</h3>
      <p className={styles.body}>{rm('customerpage_returns_products_body')}</p>
      {order && (
        <ExtendedInfo
          order={order}
          displayMode={DisplayMode.Returns}
          returnProductChanged={p => productChanged(p, internalDispatch)}
          className={styles.products}
        />
      )}
      {internalState.productsError && (
        <BlueSectionContainer error={true}>{internalState.productsError}</BlueSectionContainer>
      )}
      <h3>{rm('customerpage_returns_address_header')}</h3>
      <p className={styles.body}>{rm('customerpage_returns_address_body')}</p>
      <BlueSectionContainer error={internalState.addressError}>
        <Address
          address={internalState.address}
          email={internalState.email}
          onChange={(address, email) => addressChanged(address, email, internalDispatch)}
          changeMode={addressChange}
          toggleChange={toggleAddressChange}
          validationError={() => {
            addressLookupValidationError(internalDispatch);
          }}
        />
      </BlueSectionContainer>
      {internalState.addressError && <ErrorContainer>{internalState.addressError}</ErrorContainer>}
      <h3>{rm('customerservice_question_return_reason')}</h3>
      <BlueSectionContainer error={internalState.reasonForReturnError}>
        <strong>{rm('CustomerService_Question_Return_Reason_Label')}</strong>
        <RadioBtnContainer>
          {getReturnReasons(rm).map(item => (
            <RadioButton
              key={item.id}
              value={item.value}
              label={item.label}
              name="reasons-radio-button"
              checked={reasonIsChecked(item.value)}
              onChange={val => handleReasonChange(val, internalDispatch)}
            />
          ))}
        </RadioBtnContainer>
      </BlueSectionContainer>
      {internalState.reasonForReturnError && (
        <ErrorContainer>{internalState.reasonForReturnError}</ErrorContainer>
      )}
      <BlueSectionContainer error={internalState.itemConditionError}>
        <strong>{rm('CustomerService_Question_Return_Condition_Label')}</strong>
        <RadioBtnContainer>
          {getItemConditions(rm).map(item => (
            <RadioButton
              key={item.id}
              value={item.value}
              label={item.label}
              name="condition-radio-button"
              checked={conditionIsChecked(item.value)}
              onChange={val => handleConditionChange(val, internalDispatch)}
            />
          ))}
        </RadioBtnContainer>
      </BlueSectionContainer>
      {internalState.itemConditionError && (
        <ErrorContainer>{internalState.itemConditionError}</ErrorContainer>
      )}
      <BlueSectionContainer>
        <div className={styles.msgHeader}>
          <strong>{rm('CustomerService_Return_Other_Label')}</strong>{' '}
          <p>{rm('CustomerService_Return_Optional_Label')}</p>
        </div>
        <TextArea
          onChange={handleMsgInput}
          textlimit={OPTIONAL_MSG_MAX_LENGTH}
          value={internalState.optionalMsg}
        />
        <p
          id={styles.returnMsgLength}
        >{`(${internalState.optionalMsg.length}/${OPTIONAL_MSG_MAX_LENGTH})`}</p>
      </BlueSectionContainer>
      <BlueSectionContainer error={internalState.imagesError}>
        <strong>{rm('customerpage_returns_images_header')}</strong>
        <p>{rm('customerpage_returns_images_body')}</p>
        <DynamicImageUploader
          images={internalState.images}
          maxImageCount={MAX_IMAGE_COUNT}
          dispatch={internalDispatch}
        />
      </BlueSectionContainer>
      {internalState.imagesError && <ErrorContainer>{internalState.imagesError}</ErrorContainer>}
      <CustomerContact
        contact={internalState.contactOption}
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
      />
      <p className={styles.disclaimer}>{rm('customerpage_returns_disclaimer_01')}</p>
      <p className={styles.disclaimer}>{rm('customerpage_returns_disclaimer_02')}</p>
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error={true}>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
